import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  createGugikLayers,
  GugikLayerPanel,
  useGugikLoading,
  useGugikState,
  useGugikToggle,
} from "@/components/map/gugik";
import {
  type AreaMapKind,
  KIND_LABELS,
  ORDER_GROUP_LABELS,
  ORDER_GROUPS,
  type AreaMapBase,
  type AreaMapContractor,
  type AreaMapProject,
} from "./api";
import {
  boundsOf,
  mergedPolygons,
  outlineOf,
  voivodeshipBorders,
  type CountyFeature,
  type PolygonCoords,
  type Position,
} from "./geometry";
import {
  countyPresenceIndex,
  isAcceptedOutsideArea,
  outsideAcceptedCounties,
  projectPins,
  type CountyPresence,
  type KindFilter,
  type PinStatus,
  type ProjectPin,
} from "./selection";

export interface AreaMapLayers {
  countyLines: boolean;
  voivodeshipLines: boolean;
  fills: boolean;
  bases: boolean;
  /** Powiaty z przyjętymi zamówieniami firmy spoza jej obszaru działania. */
  outsideOrders: boolean;
  /** Pinezki projektów (warstwa nadrzędna) i jej podfiltry po stanie zamówień. */
  projects: boolean;
  pinsAccepted: boolean;
  pinsPending: boolean;
  pinsMixed: boolean;
  /** Aktywne projekty bez zamówień pokazanych firm (szare). */
  pinsEmpty: boolean;
}

/** Podcheckbox warstwy dla stanu pinezki. */
export const PIN_STATUS_LAYER: Record<PinStatus, keyof AreaMapLayers> = {
  accepted: "pinsAccepted",
  pending: "pinsPending",
  mixed: "pinsMixed",
  empty: "pinsEmpty",
};

interface Props {
  counties: CountyFeature[];
  /** Wszystkie firmy megamapy — kształty obszarów liczą się raz, nie przy każdym kliknięciu. */
  contractors: AreaMapContractor[];
  /** Firmy pokazane na mapie (zaznaczone na liście i zgodne z filtrem rodzaju). */
  visibleIds: Set<string>;
  colors: Map<string, string>;
  bases: AreaMapBase[];
  /** Pinezki projektów (wszystkich firm megamapy — filtruje kanwa). */
  projects: AreaMapProject[];
  /** Filtr rodzaju z listy (kilka naraz) — do pinezek wchodzą tylko zamówienia tych rodzajów. */
  kind: KindFilter;
  layers: AreaMapLayers;
  /** Firma wskazana na liście — jej obszar wyróżniony, reszta przygaszona. */
  highlightId: string | null;
  /** Zmiana `key` = przybliż mapę do obszaru (albo siedziby) firmy. */
  focus: { id: string; key: number } | null;
}

interface ContractorShape {
  polygons: PolygonCoords[];
  outline: Position[][];
}

function shapeOf(byCode: Map<string, CountyFeature>, codes: string[]): ContractorShape | null {
  if (codes.length === 0) return null;
  const polygons = mergedPolygons(byCode, codes);
  return { polygons, outline: outlineOf(polygons) };
}

// Panes above the GUGiK overlays (baseZ 300…315) and below Leaflet's tooltip pane (650).
const PANES: Record<string, number> = {
  areaFill: 410,
  areaOutline: 420,
  adminLines: 430,
  countyHover: 440,
  projects: 445,
  bases: 450,
};

/** Status zamówienia w dymku pinezki. */
const ACCEPTED_COLOR = "#16a34a";
const PENDING_COLOR = "#b45309";
/** Aktywny projekt bez zamówień pokazanych firm. */
export const EMPTY_PIN_COLOR = "#9ca3af";

// SVG, not a CSS shape: index.css zeroes border-radius on every element (brutalist UI),
// which would flatten a CSS teardrop into a square.
const PIN_PATH = "M12 1C6 1 1 5.9 1 12c0 8.2 11 19 11 19s11-10.8 11-19C23 5.9 18 1 12 1z";
// Every pin defines the same clip path; duplicate ids resolve to identical geometry.
const PIN_CLIP_ID = "area-map-pin-clip";

/** Pinezka jako SVG w kolorach firm: jedna firma = cała kropla, kilka = pionowe pasy
 * (w kolejności listy); bez kolorów = szara (projekt bez zamówień). */
export function pinSvg(colors: string[], size: number, opacity = 1): string {
  const height = Math.round((size * 32) / 24);
  const fills = colors.length ? colors : [EMPTY_PIN_COLOR];
  const width = 24 / fills.length;
  const stripes = fills
    .map((c, i) => `<rect x="${(i * width).toFixed(3)}" y="0" width="${(width + 0.05).toFixed(3)}" height="32" fill="${c}"/>`)
    .join("");
  return `<svg width="${size}" height="${height}" viewBox="0 0 24 32" style="display:block;opacity:${opacity};filter:drop-shadow(0 1px 2px rgba(0,0,0,.45))"><defs><clipPath id="${PIN_CLIP_ID}"><path d="${PIN_PATH}"/></clipPath></defs><g clip-path="url(#${PIN_CLIP_ID})">${stripes}</g><path d="${PIN_PATH}" fill="none" stroke="#fff" stroke-width="2"/><circle cx="12" cy="12" r="4" fill="#fff"/></svg>`;
}

/** Ten sam glif w legendzie strony. */
export function PinGlyph({ colors, size = 12 }: { colors: string[]; size?: number }) {
  return <span className="inline-block" dangerouslySetInnerHTML={{ __html: pinSvg(colors, size) }} />;
}

function pinIcon(colors: string[], emphasis: "normal" | "lit" | "dim"): L.DivIcon {
  const size = emphasis === "lit" ? 30 : colors.length ? 24 : 18;
  const height = Math.round((size * 32) / 24);
  return L.divIcon({
    html: pinSvg(colors, size, emphasis === "dim" ? 0.35 : 1),
    className: "",
    iconSize: [size, height],
    // The tip of the drop is the bottom of the viewBox.
    iconAnchor: [size / 2, height],
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

const HOVER_FILL = { fillColor: "#0f172a", fillOpacity: 0.06 };
/** Dashed outline = accepted orders outside the declared area (solid = the area itself). */
const OUTSIDE_DASH = "7 5";
const WARNING_BADGE =
  '<span style="display:inline-flex;align-items:center;justify-content:center;width:15px;height:15px;border-radius:9999px;background:#dc2626;color:#fff;font-weight:700;font-size:11px;line-height:1;flex-shrink:0;margin-top:1px" title="Przyjęte zamówienie poza obszarem działania">!</span>';

/** Dymek powiatu: firmy z obszarem działania albo z aktywnymi zamówieniami w nim. */
function countyTooltipHtml(name: string, presence: CountyPresence[], colors: Map<string, string>): string {
  const label = name.startsWith("m. ") ? name : `pow. ${name}`;
  const rows = presence.length
    ? presence
        .map((p) => {
          const { contractor: c, inArea, orders } = p;
          const color = colors.get(c.id) ?? "#64748b";
          const kinds = c.kinds.map((k) => KIND_LABELS[k]).join(", ");
          const counts = ORDER_GROUPS.filter((g) => orders[g])
            .map((g) => `${ORDER_GROUP_LABELS[g]} <b>${orders[g]}</b> zam.`)
            .join(" · ");
          const warning = isAcceptedOutsideArea(p);
          const outside = inArea
            ? ""
            : warning
              ? `<span style="color:#b91c1c;font-weight:600">przyjęte zamówienie poza obszarem działania</span>`
              : `<span style="color:#b45309">poza obszarem działania (zamówienie jeszcze nieprzyjęte)</span>`;
          const detail = [
            counts || `<span style="color:#94a3b8">brak aktywnych zamówień</span>`,
            outside,
          ]
            .filter(Boolean)
            .join(" · ");
          return `<div style="display:flex;gap:6px;margin-top:3px"><span style="width:10px;height:10px;border-radius:2px;background:${color};flex-shrink:0;margin-top:3px"></span>${warning ? WARNING_BADGE : ""}<div>${escapeHtml(c.name)} <span style="color:#64748b">(${kinds})</span><div style="color:#334155">${detail}</div></div></div>`;
        })
        .join("")
    : `<div style="color:#64748b">żadna z pokazanych firm</div>`;
  // width:max-content — the tooltip pane has no width, so wrapping text would otherwise
  // shrink to its longest word.
  return `<div style="font-size:12px;line-height:1.4;width:max-content;max-width:340px;white-space:normal"><b>${escapeHtml(label)}</b>${rows}</div>`;
}

/** Dymek pinezki: projekt i aktywne zamówienia pokazanych firm z ich statusem. */
const KIND_FILTER_SUFFIX: Record<AreaMapKind, string> = {
  wpw: "WPW",
  surveyor: "na mapy",
  surveyor_wpw: "na mapy i WPW",
  coordinator: "na prowadzenie",
};

function projectTooltipHtml(
  pin: ProjectPin,
  byId: Map<string, AreaMapContractor>,
  colors: Map<string, string>,
  kind: KindFilter,
): string {
  const rows = pin.orders
    .map((o) => {
      const color = colors.get(o.contractor) ?? "#64748b";
      const name = byId.get(o.contractor)?.name ?? "—";
      const statusColor = o.accepted ? ACCEPTED_COLOR : PENDING_COLOR;
      return `<div style="display:flex;gap:6px;margin-top:3px"><span style="width:10px;height:10px;border-radius:2px;background:${color};flex-shrink:0;margin-top:3px"></span><div>${escapeHtml(name)}<div style="color:#334155">${ORDER_GROUP_LABELS[o.group]} · ${escapeHtml(o.number)} · <span style="color:${statusColor};font-weight:600">${escapeHtml(o.status_label)}</span></div></div></div>`;
    })
    .join("");
  const suffix = kind.length ? ` (${kind.map((k) => KIND_FILTER_SUFFIX[k]).join(" / ")})` : "";
  const empty = `<div style="color:#64748b;margin-top:2px">brak aktywnych zamówień${suffix} pokazanych firm</div>`;
  return `<div style="font-size:12px;line-height:1.4;width:max-content;max-width:340px;white-space:normal"><b>${escapeHtml(pin.project.name)}</b>${rows || empty}<div style="color:#94a3b8;margin-top:4px">kliknij, aby otworzyć projekt</div></div>`;
}

/** Lżejsze wypełnienie, gdy na mapie jest dużo nakładających się firm. */
function fillOpacityFor(count: number): number {
  if (count <= 3) return 0.22;
  if (count <= 8) return 0.15;
  return 0.1;
}

export function ContractorAreaCanvas({
  counties,
  contractors,
  visibleIds,
  colors,
  bases,
  projects,
  kind,
  layers,
  highlightId,
  focus,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const countyLayerRef = useRef<L.GeoJSON | null>(null);
  const voivodeshipLayerRef = useRef<L.Polyline | null>(null);
  const areasRef = useRef<L.LayerGroup | null>(null);
  const basesRef = useRef<L.LayerGroup | null>(null);
  const projectsRef = useRef<L.LayerGroup | null>(null);
  const [zoom, setZoom] = useState(6);

  const gugikParcelsRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikInfraRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikOrthoRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikMpzpRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGdosRef = useRef<L.TileLayer.WMS | null>(null);
  const [gugik, setGugik] = useGugikState("contractors.areaMap", {
    parcels: false, infra: false, ortho: false, mpzp: false, gdos: false,
  });

  const byCode = useMemo(
    () => new Map(counties.map((f) => [f.properties.teryt, f])),
    [counties],
  );

  // Geometry per contractor is the expensive part (edge counting) — computed once per data
  // load, not on every checkbox toggle or hover.
  const shapes = useMemo(() => {
    const out = new Map<string, ContractorShape>();
    for (const c of contractors) {
      const shape = shapeOf(byCode, c.county_codes);
      if (shape) out.set(c.id, shape);
    }
    return out;
  }, [contractors, byCode]);
  const outsideShapes = useMemo(() => {
    const out = new Map<string, ContractorShape>();
    for (const c of contractors) {
      const shape = shapeOf(byCode, outsideAcceptedCounties(c));
      if (shape) out.set(c.id, shape);
    }
    return out;
  }, [contractors, byCode]);

  const shown = useMemo(
    () => contractors.filter((c) => visibleIds.has(c.id)),
    [contractors, visibleIds],
  );

  // County code → shown firms there (area or active orders), read by the hover handlers
  // registered once on the county layer.
  const presenceRef = useRef(new Map<string, CountyPresence[]>());
  presenceRef.current = useMemo(() => countyPresenceIndex(shown), [shown]);
  const colorsRef = useRef(colors);
  colorsRef.current = colors;

  // ONE tooltip for the whole map, opened and closed by hand. Per-layer tooltips were left
  // behind when the map was dragged (Leaflet does not always fire mouseout mid-drag), so
  // several could stay open at once.
  const tipRef = useRef<L.Tooltip | null>(null);
  const hoveredRef = useRef<L.Path | null>(null);
  const movingRef = useRef(false);
  const clearHover = () => {
    hoveredRef.current?.setStyle({ fillOpacity: 0 });
    hoveredRef.current = null;
    tipRef.current?.close();
  };
  const showTip = (content: string, latlng: L.LatLng) => {
    const map = mapRef.current;
    const tip = tipRef.current;
    if (!map || !tip) return;
    tip.setLatLng(latlng);
    if (content) tip.setContent(content);
    if (!map.hasLayer(tip)) tip.openOn(map);
  };

  // ── init map (once) ──
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { center: [52.0, 19.3], zoom: 6, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21, zIndex: 0 }).addTo(map);
    const g = createGugikLayers(map);
    gugikParcelsRef.current = g.parcels;
    gugikInfraRef.current = g.infra;
    gugikOrthoRef.current = g.ortho;
    gugikMpzpRef.current = g.mpzp;
    gugikGdosRef.current = g.gdos;
    for (const [name, z] of Object.entries(PANES)) {
      map.createPane(name).style.zIndex = String(z);
    }
    areasRef.current = L.layerGroup().addTo(map);
    basesRef.current = L.layerGroup().addTo(map);
    projectsRef.current = L.layerGroup().addTo(map);
    tipRef.current = L.tooltip({ direction: "top", offset: [0, -10], opacity: 0.96 });
    map.on("zoomend", () => setZoom(map.getZoom()));
    // Any pan / zoom / fly hides the tooltip; the next mousemove after the move shows it again.
    map.on("movestart zoomstart", () => {
      movingRef.current = true;
      clearHover();
    });
    map.on("moveend", () => {
      movingRef.current = false;
    });
    map.getContainer().addEventListener("mouseleave", clearHover);
    mapRef.current = map;
    // The map mounts inside a flex row — measure once layout has settled so tiles fill it.
    setTimeout(() => map.invalidateSize(), 0);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useGugikToggle(mapRef, gugikParcelsRef, gugik.parcels);
  useGugikToggle(mapRef, gugikInfraRef, gugik.infra);
  useGugikToggle(mapRef, gugikOrthoRef, gugik.ortho);
  useGugikToggle(mapRef, gugikMpzpRef, gugik.mpzp);
  useGugikToggle(mapRef, gugikGdosRef, gugik.gdos);
  const gugikLoading = useGugikLoading(
    gugikParcelsRef, gugikInfraRef, gugikOrthoRef, gugik, gugikMpzpRef, gugikGdosRef,
  );

  // ── administrative boundaries: county hover layer + voivodeship borders ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || counties.length === 0) return;
    const countyLayer = L.geoJSON(
      { type: "FeatureCollection", features: counties } as never,
      {
        pane: "countyHover",
        // Transparent fill keeps the whole county hoverable; the stroke is the toggle.
        style: { color: "#64748b", weight: 0.6, opacity: 0.8, fill: true, fillOpacity: 0 },
        onEachFeature: (feature, layer) => {
          const { teryt, name } = (feature as CountyFeature).properties;
          const path = layer as L.Path;
          // mousemove drives it (not only mouseover): after a drag the cursor already sits
          // inside a county and no mouseover follows.
          const hover = (e: L.LeafletMouseEvent) => {
            if (movingRef.current) return;
            if (hoveredRef.current !== path) {
              clearHover();
              hoveredRef.current = path;
              path.setStyle(HOVER_FILL);
              showTip(
                countyTooltipHtml(name, presenceRef.current.get(teryt) ?? [], colorsRef.current),
                e.latlng,
              );
            } else {
              showTip("", e.latlng);
            }
          };
          path.on("mouseover", hover);
          path.on("mousemove", hover);
          path.on("mouseout", () => {
            if (hoveredRef.current === path) clearHover();
          });
        },
      },
    ).addTo(map);
    countyLayerRef.current = countyLayer;

    const borders = voivodeshipBorders(counties);
    const voivodeshipLayer = L.polyline(
      borders.map((line) => line.map(([lng, lat]) => [lat, lng] as L.LatLngTuple)),
      { pane: "adminLines", color: "#334155", weight: 1.8, opacity: 0.75, interactive: false },
    ).addTo(map);
    voivodeshipLayerRef.current = voivodeshipLayer;

    return () => {
      clearHover();
      countyLayer.remove();
      voivodeshipLayer.remove();
      countyLayerRef.current = null;
      voivodeshipLayerRef.current = null;
    };
  }, [counties]);

  useEffect(() => {
    countyLayerRef.current?.setStyle({ weight: layers.countyLines ? 0.6 : 0 });
  }, [layers.countyLines, counties]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = voivodeshipLayerRef.current;
    if (!map || !layer) return;
    if (layers.voivodeshipLines) layer.addTo(map);
    else layer.remove();
  }, [layers.voivodeshipLines, counties]);

  // ── contractor areas ──
  useEffect(() => {
    const group = areasRef.current;
    if (!group) return;
    group.clearLayers();
    const fillOpacity = fillOpacityFor(
      shown.filter((c) => shapes.has(c.id) || (layers.outsideOrders && outsideShapes.has(c.id))).length,
    );
    // The highlighted area is drawn last so its outline sits on top of the neighbours'.
    const ordered = highlightId
      ? [...shown.filter((c) => c.id !== highlightId), ...shown.filter((c) => c.id === highlightId)]
      : shown;
    for (const c of ordered) {
      const color = colors.get(c.id) ?? "#64748b";
      const dimmed = highlightId !== null && highlightId !== c.id;
      const lit = highlightId === c.id;
      const draw = (shape: ContractorShape, dashArray?: string) => {
        if (layers.fills || lit) {
          // One MultiPolygon = one SVG path, so adjacent counties leave no anti-aliasing seams.
          L.geoJSON({ type: "MultiPolygon", coordinates: shape.polygons } as never, {
            pane: "areaFill",
            interactive: false,
            style: {
              stroke: false,
              fillColor: color,
              fillOpacity: lit ? 0.38 : dimmed ? 0.04 : fillOpacity,
            },
          }).addTo(group);
        }
        L.polyline(
          shape.outline.map((line) => line.map(([lng, lat]) => [lat, lng] as L.LatLngTuple)),
          {
            pane: "areaOutline",
            interactive: false,
            color,
            dashArray,
            weight: lit ? 4 : 2.5,
            opacity: dimmed ? 0.3 : 0.95,
          },
        ).addTo(group);
      };
      const area = shapes.get(c.id);
      if (area) draw(area);
      const outside = outsideShapes.get(c.id);
      if (outside && layers.outsideOrders) draw(outside, OUTSIDE_DASH);
    }
  }, [shown, shapes, outsideShapes, colors, layers.fills, layers.outsideOrders, highlightId]);

  // ── project pins (active orders of the shown firms) ──
  useEffect(() => {
    const group = projectsRef.current;
    if (!group) return;
    // Same as the pins below: an open tooltip with no hovered county belongs to a marker
    // that is about to be removed.
    if (!hoveredRef.current) tipRef.current?.close();
    group.clearLayers();
    if (!layers.projects) return;
    const byId = new Map(contractors.map((c) => [c.id, c]));
    const listOrder = new Map(contractors.map((c, i) => [c.id, i]));
    const pins = projectPins(projects, visibleIds, kind, listOrder)
      .filter((pin) => layers[PIN_STATUS_LAYER[pin.status]])
      // Grey pins first, so coloured ones are drawn on top of them.
      .sort((a, b) => Number(b.status === "empty") - Number(a.status === "empty"));
    for (const pin of pins) {
      const emphasis = !highlightId
        ? "normal"
        : pin.contractors.includes(highlightId)
          ? "lit"
          : "dim";
      const marker = L.marker([pin.project.lat, pin.project.lng], {
        pane: "projects",
        icon: pinIcon(
          pin.contractors.map((id) => colors.get(id) ?? EMPTY_PIN_COLOR),
          emphasis,
        ),
        keyboard: false,
      });
      marker.on("mouseover", () => {
        if (movingRef.current) return;
        clearHover();
        showTip(projectTooltipHtml(pin, byId, colors, kind), marker.getLatLng());
      });
      marker.on("mouseout", clearHover);
      marker.on("click", () =>
        window.open(`/power-projects/${pin.project.project}`, "_blank", "noopener"),
      );
      marker.addTo(group);
    }
  }, [projects, visibleIds, kind, contractors, colors, layers, highlightId]);

  // ── bases (siedziby / bazy wypadowe) ──
  useEffect(() => {
    const group = basesRef.current;
    if (!group) return;
    // An open tooltip with no hovered county belongs to a pin that is about to be removed
    // (and would never get its mouseout).
    if (!hoveredRef.current) tipRef.current?.close();
    group.clearLayers();
    if (!layers.bases) return;
    const byId = new Map(shown.map((c) => [c.id, c]));
    for (const b of bases) {
      const c = byId.get(b.contractor);
      if (!c) continue;
      const lit = highlightId === c.id;
      const marker = L.circleMarker([b.lat, b.lng], {
        pane: "bases",
        radius: lit ? 8 : 6,
        color: "#ffffff",
        weight: 2,
        fillColor: colors.get(c.id) ?? "#64748b",
        fillOpacity: highlightId && !lit ? 0.45 : 1,
      });
      // Pins share the map's single tooltip with the counties.
      marker.on("mouseover", () => {
        if (movingRef.current) return;
        clearHover();
        showTip(
          `<div style="font-size:12px"><b>${escapeHtml(c.name)}</b><br><span style="color:#64748b">${escapeHtml(b.label)}</span></div>`,
          marker.getLatLng(),
        );
      });
      marker.on("mouseout", clearHover);
      marker.addTo(group);
    }
  }, [bases, shown, colors, layers.bases, highlightId]);

  // ── focus: zoom to a contractor's area (or its base when it has none) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    const box = boundsOf([
      ...(shapes.get(focus.id)?.polygons ?? []),
      ...(layers.outsideOrders ? (outsideShapes.get(focus.id)?.polygons ?? []) : []),
    ]);
    if (box) {
      map.flyToBounds(box, { padding: [30, 30], maxZoom: 10 });
      return;
    }
    const base = bases.find((b) => b.contractor === focus.id);
    if (base) map.flyTo([base.lat, base.lng], 9);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.key]);

  return (
    // `isolate` contains the Leaflet/GUGiK z-indexes (panes up to 650, controls and our
    // panels at 1000) inside the map box, so the notifications dropdown and modals paint
    // above the map — same as MapTab / ZEDrawingMap.
    <div className="relative isolate z-0 h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      {/* `top-[78px]` = pod leafletowym +/− — jak na Megamapie ZE. */}
      <GugikLayerPanel
        gugik={gugik}
        onChange={setGugik}
        zoom={zoom}
        loading={gugikLoading}
        className="absolute top-[78px] left-2"
      />
    </div>
  );
}
